import type { PartyCheckInErrorKind, PartyCheckInResult } from "./checkInParty";

type Translate = (key: string) => string;

/**
 * One sentence for one failed check-in, for every door that writes one.
 *
 * There are five synchronous doors onto `PUT /check-in-out` — the admin picker,
 * the party sheet, the guest-detail drawer, the guest-list row cell and the
 * check-in-history row. Each of them had its own hand-written kind→string
 * ternary, and this chain spent several rounds discovering that a rule applied
 * to one of them is a rule missing from four: the neutral refusal wording
 * landed on one door, the warning colour on another, and each time the commit
 * message said "all doors".
 *
 * So the mapping lives here. A new `PartyCheckInErrorKind` is a compile error
 * in one place instead of a silent fallback to "unknown" in five.
 *
 * `notAllowedKey` is the one deliberate difference: a door that writes ONE
 * guest, or writes in both directions, must not use the party sheet's
 * `checkInParty.notAllowed` — that string is plural and says "check these
 * guests IN". Such a door passes nothing and gets the neutral sentence.
 */
export function checkInFailureText(
  error: PartyCheckInErrorKind | undefined,
  t: {
    checkIn: Translate;
    errors: Translate;
    party: Translate;
  },
  opts: { notAllowedFromParty?: boolean } = {},
): string {
  switch (error) {
    case "capacity":
      return t.checkIn("capacityExceeded");
    case "rateLimited":
      return t.errors("rateLimited");
    case "eventArchived":
      return t.errors("eventArchivedReadOnly");
    case "eventPlanLocked":
      return t.errors("eventPlanLocked");
    case "notAllowed":
      return opts.notAllowedFromParty
        ? t.party("notAllowed")
        : t.errors("insufficientCapability");
    case "outOfScope":
      return t.party("outOfScope");
    case "unknown":
    case undefined:
      return t.errors("unknown");
    default: {
      // A new kind is decided HERE, not defaulted. Without this the switch fell
      // through to "unknown" in all five doors and the only thing catching a
      // new kind was `stopsTheRun`'s `never` in a different file — while this
      // file's own docstring claimed the compile error.
      // Compile-time exhaustiveness WITHOUT making the runtime worse: returning
      // `exhaustive` would print the raw kind at a door. The `never` is the
      // guard; the sentence below is what a value that got past the types
      // anyway should still say.
      const exhaustive: never = error;
      void exhaustive;
      return t.errors("unknown");
    }
  }
}

/** The same answer for a whole result, so `already`/`done` cannot be mistaken. */
export function checkInResultText(
  result: PartyCheckInResult | undefined,
  t: { checkIn: Translate; errors: Translate; party: Translate },
  opts: { notAllowedFromParty?: boolean } = {},
): string | null {
  if (!result) return null;
  if (result.status === "already") return t.party("alreadyInState");
  if (result.status === "done") {
    return result.warning === "capacity" ? t.checkIn("capacityExceeded") : null;
  }
  return checkInFailureText(result.error, t, opts);
}
